<script setup>
/**
 * SzDatePicker — date picker SolarZ (substitui o picker nativo do browser).
 * Design baseado no Figma: card branco/dark com sombra, header de mês/ano,
 * grid de 7×6, footer com Limpar/Hoje. Cor primária: #388E3C.
 *
 * Props:
 *   modelValue : string "YYYY-MM-DD" (vazio = sem seleção)
 *   placeholder: texto exibido quando vazio (default "dd/mm/aaaa")
 *   min, max   : strings "YYYY-MM-DD" (opcional — desabilitam datas fora do range)
 *   clearable  : boolean (default true) — mostra botão "Limpar" no footer
 *   disabled   : boolean
 *
 * Emits:
 *   update:modelValue (v-model)
 */
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue';

const props = defineProps({
  modelValue: { type: String, default: '' },
  placeholder: { type: String, default: 'dd/mm/aaaa' },
  min: { type: String, default: '' },
  max: { type: String, default: '' },
  clearable: { type: Boolean, default: true },
  disabled: { type: Boolean, default: false },
});
const emit = defineEmits(['update:modelValue']);

const WEEKDAYS = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];
const MONTHS = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

// ────────────────── helpers ──────────────────
const pad = n => String(n).padStart(2, '0');
const parseIso = iso => {
  if (!iso) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return null;
  const [, y, mo, d] = m;
  return new Date(Number(y), Number(mo) - 1, Number(d));
};
const toIso = d => (d ? `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` : '');
const toDisplay = d => (d ? `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}` : '');
const sameDay = (a, b) =>
  a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const startOfMonth = d => new Date(d.getFullYear(), d.getMonth(), 1);
const addMonths = (d, n) => new Date(d.getFullYear(), d.getMonth() + n, 1);
const today = () => {
  const t = new Date();
  return new Date(t.getFullYear(), t.getMonth(), t.getDate());
};

const minDate = computed(() => parseIso(props.min));
const maxDate = computed(() => parseIso(props.max));
const isDisabled = d => {
  if (minDate.value && d < minDate.value) return true;
  if (maxDate.value && d > maxDate.value) return true;
  return false;
};

// ────────────────── estado ──────────────────
const selectedDate = computed(() => parseIso(props.modelValue));
const displayDate = computed(() => toDisplay(selectedDate.value));
const isOpen = ref(false);
const viewMonth = ref(startOfMonth(selectedDate.value || today()));
const rootEl = ref(null);
const popoverEl = ref(null);
const popoverStyle = ref({});

watch(
  () => props.modelValue,
  () => {
    if (selectedDate.value) viewMonth.value = startOfMonth(selectedDate.value);
  }
);

// ────────────────── grid 6×7 ──────────────────
const weeks = computed(() => {
  const first = viewMonth.value;
  const dayOfWeekFirst = first.getDay(); // 0=Dom
  const start = new Date(first);
  start.setDate(first.getDate() - dayOfWeekFirst);
  const result = [];
  const sel = selectedDate.value;
  const tdy = today();
  for (let w = 0; w < 6; w++) {
    const row = [];
    for (let d = 0; d < 7; d++) {
      const cur = new Date(start);
      cur.setDate(start.getDate() + (w * 7 + d));
      row.push({
        date: cur,
        day: cur.getDate(),
        inMonth: cur.getMonth() === first.getMonth(),
        isToday: sameDay(cur, tdy),
        isSelected: sel && sameDay(cur, sel),
        disabled: isDisabled(cur),
      });
    }
    result.push(row);
  }
  return result;
});

const monthLabel = computed(() => MONTHS[viewMonth.value.getMonth()]);
const yearLabel = computed(() => viewMonth.value.getFullYear());
const isMonthPickerOpen = ref(false);
const isYearPickerOpen = ref(false);
const yearList = computed(() => {
  const current = viewMonth.value.getFullYear();
  const minY = minDate.value ? minDate.value.getFullYear() : current - 10;
  const maxY = maxDate.value ? maxDate.value.getFullYear() : current + 10;
  const arr = [];
  for (let y = maxY; y >= minY; y--) arr.push(y);
  return arr;
});
function toggleMonthPicker() {
  isMonthPickerOpen.value = !isMonthPickerOpen.value;
  isYearPickerOpen.value = false;
}
function toggleYearPicker() {
  isYearPickerOpen.value = !isYearPickerOpen.value;
  isMonthPickerOpen.value = false;
}
function pickMonth(m) {
  viewMonth.value = new Date(viewMonth.value.getFullYear(), m, 1);
  isMonthPickerOpen.value = false;
}
function pickYear(y) {
  viewMonth.value = new Date(y, viewMonth.value.getMonth(), 1);
  isYearPickerOpen.value = false;
}

// ────────────────── ações ──────────────────
function open() {
  if (props.disabled) return;
  isOpen.value = true;
  // ajusta viewMonth pro modelValue atual (caso tenha mudado externamente)
  if (selectedDate.value) viewMonth.value = startOfMonth(selectedDate.value);
  nextTick(positionPopover);
}
function close() {
  isOpen.value = false;
}
function toggle() {
  isOpen.value ? close() : open();
}
function prevMonth() {
  viewMonth.value = addMonths(viewMonth.value, -1);
}
function nextMonth() {
  viewMonth.value = addMonths(viewMonth.value, 1);
}
function pickDate(cell) {
  if (cell.disabled) return;
  emit('update:modelValue', toIso(cell.date));
  close();
}
function pickToday() {
  const t = today();
  if (isDisabled(t)) return;
  emit('update:modelValue', toIso(t));
  close();
}
function clearDate() {
  emit('update:modelValue', '');
  close();
}

function positionPopover() {
  if (!rootEl.value || !popoverEl.value) return;
  const rect = rootEl.value.getBoundingClientRect();
  const popH = 420;
  const popW = 304;
  const margin = 8;
  const vh = window.innerHeight;
  const vw = window.innerWidth;
  let top = rect.bottom + margin;
  if (top + popH > vh) {
    top = Math.max(margin, rect.top - popH - margin);
  }
  let left = rect.left;
  if (left + popW > vw - margin) left = Math.max(margin, vw - popW - margin);
  popoverStyle.value = {
    position: 'fixed',
    top: `${top}px`,
    left: `${left}px`,
    zIndex: 9999,
  };
}

function onPopoverMousedown(e) {
  e.stopPropagation();
  // se clicou fora de qualquer sub-dropdown de mês/ano, fecha eles
  if (!e.target.closest || !e.target.closest('.sz-dp-select-wrap')) {
    isMonthPickerOpen.value = false;
    isYearPickerOpen.value = false;
  }
}

function onClickOutside(e) {
  if (!isOpen.value) return;
  const path = e.composedPath ? e.composedPath() : [];
  if (rootEl.value && (path.includes(rootEl.value) || rootEl.value.contains(e.target))) return;
  if (popoverEl.value && (path.includes(popoverEl.value) || popoverEl.value.contains(e.target))) return;
  close();
}
function onKeydown(e) {
  if (!isOpen.value) return;
  if (e.key === 'Escape') close();
}
function onScrollOrResize() {
  if (isOpen.value) positionPopover();
}

onMounted(() => {
  document.addEventListener('mousedown', onClickOutside);
  document.addEventListener('keydown', onKeydown);
  window.addEventListener('scroll', onScrollOrResize, true);
  window.addEventListener('resize', onScrollOrResize);
});
onUnmounted(() => {
  document.removeEventListener('mousedown', onClickOutside);
  document.removeEventListener('keydown', onKeydown);
  window.removeEventListener('scroll', onScrollOrResize, true);
  window.removeEventListener('resize', onScrollOrResize);
});
</script>

<template>
  <div ref="rootEl" class="sz-dp">
    <button
      type="button"
      class="sz-dp-trigger"
      :class="{ 'sz-dp-trigger--empty': !displayDate, 'sz-dp-trigger--disabled': disabled }"
      :disabled="disabled"
      @click="toggle"
    >
      <span class="sz-dp-trigger-text">{{ displayDate || placeholder }}</span>
      <svg class="sz-dp-trigger-icon" width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M1.66675 10C1.66675 6.85731 1.66675 5.28596 2.64306 4.30965C3.61937 3.33334 5.19072 3.33334 8.33341 3.33334H11.6667C14.8094 3.33334 16.3808 3.33334 17.3571 4.30965C18.3334 5.28596 18.3334 6.85731 18.3334 10V11.6667C18.3334 14.8094 18.3334 16.3807 17.3571 17.357C16.3808 18.3333 14.8094 18.3333 11.6667 18.3333H8.33341C5.19072 18.3333 3.61937 18.3333 2.64306 17.357C1.66675 16.3807 1.66675 14.8094 1.66675 11.6667V10Z" stroke="currentColor" stroke-width="1.5"/>
        <path d="M5.83325 3.33334V2.08334" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
        <path d="M14.1667 3.33334V2.08334" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
        <path d="M2.08325 7.5H17.9166" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
        <path d="M14.9999 14.1667C14.9999 14.6269 14.6268 15 14.1666 15C13.7063 15 13.3333 14.6269 13.3333 14.1667C13.3333 13.7064 13.7063 13.3333 14.1666 13.3333C14.6268 13.3333 14.9999 13.7064 14.9999 14.1667Z" fill="currentColor"/>
        <path d="M14.9999 10.8333C14.9999 11.2936 14.6268 11.6667 14.1666 11.6667C13.7063 11.6667 13.3333 11.2936 13.3333 10.8333C13.3333 10.3731 13.7063 10 14.1666 10C14.6268 10 14.9999 10.3731 14.9999 10.8333Z" fill="currentColor"/>
        <path d="M10.8334 14.1667C10.8334 14.6269 10.4603 15 10.0001 15C9.53984 15 9.16675 14.6269 9.16675 14.1667C9.16675 13.7064 9.53984 13.3333 10.0001 13.3333C10.4603 13.3333 10.8334 13.7064 10.8334 14.1667Z" fill="currentColor"/>
        <path d="M10.8334 10.8333C10.8334 11.2936 10.4603 11.6667 10.0001 11.6667C9.53984 11.6667 9.16675 11.2936 9.16675 10.8333C9.16675 10.3731 9.53984 10 10.0001 10C10.4603 10 10.8334 10.3731 10.8334 10.8333Z" fill="currentColor"/>
        <path d="M6.66667 14.1667C6.66667 14.6269 6.29357 15 5.83333 15C5.3731 15 5 14.6269 5 14.1667C5 13.7064 5.3731 13.3333 5.83333 13.3333C6.29357 13.3333 6.66667 13.7064 6.66667 14.1667Z" fill="currentColor"/>
        <path d="M6.66667 10.8333C6.66667 11.2936 6.29357 11.6667 5.83333 11.6667C5.3731 11.6667 5 11.2936 5 10.8333C5 10.3731 5.3731 10 5.83333 10C6.29357 10 6.66667 10.3731 6.66667 10.8333Z" fill="currentColor"/>
      </svg>
    </button>

    <Teleport to="body">
      <div v-if="isOpen" ref="popoverEl" class="sz-dp-popover" :style="popoverStyle" @mousedown="onPopoverMousedown">
        <!-- Header: dropdown mês + ano + navegação -->
        <div class="sz-dp-header">
          <div class="sz-dp-header-selects">
            <div class="sz-dp-select-wrap">
              <button
                type="button"
                class="sz-dp-select"
                :class="{ open: isMonthPickerOpen }"
                @click="toggleMonthPicker"
              >
                <span>{{ monthLabel }}</span>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
              </button>
              <div v-if="isMonthPickerOpen" class="sz-dp-select-list">
                <button
                  v-for="(m, i) in MONTHS"
                  :key="i"
                  type="button"
                  class="sz-dp-select-item"
                  :class="{ active: i === viewMonth.getMonth() }"
                  @click="pickMonth(i)"
                >{{ m }}</button>
              </div>
            </div>
            <div class="sz-dp-select-wrap">
              <button
                type="button"
                class="sz-dp-select"
                :class="{ open: isYearPickerOpen }"
                @click="toggleYearPicker"
              >
                <span>{{ yearLabel }}</span>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
              </button>
              <div v-if="isYearPickerOpen" class="sz-dp-select-list">
                <button
                  v-for="y in yearList"
                  :key="y"
                  type="button"
                  class="sz-dp-select-item"
                  :class="{ active: y === viewMonth.getFullYear() }"
                  @click="pickYear(y)"
                >{{ y }}</button>
              </div>
            </div>
          </div>
          <div class="sz-dp-header-nav">
            <button type="button" class="sz-dp-nav-btn" aria-label="Mês anterior" @click="prevMonth">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="18 15 12 9 6 15" />
              </svg>
            </button>
            <button type="button" class="sz-dp-nav-btn" aria-label="Próximo mês" @click="nextMonth">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
          </div>
        </div>

        <!-- Calendário -->
        <div class="sz-dp-calendar">
          <div class="sz-dp-weekrow">
            <div v-for="(w, i) in WEEKDAYS" :key="i" class="sz-dp-weekday">{{ w }}</div>
          </div>
          <div v-for="(row, ri) in weeks" :key="ri" class="sz-dp-week">
            <button
              v-for="(cell, ci) in row"
              :key="ci"
              type="button"
              class="sz-dp-cell"
              :class="{
                'sz-dp-cell--out': !cell.inMonth,
                'sz-dp-cell--today': cell.isToday && !cell.isSelected,
                'sz-dp-cell--selected': cell.isSelected,
                'sz-dp-cell--disabled': cell.disabled,
              }"
              :disabled="cell.disabled"
              @click="pickDate(cell)"
            >{{ cell.day }}</button>
          </div>
        </div>

        <!-- Footer: Limpar / Hoje -->
        <div class="sz-dp-footer">
          <button
            v-if="clearable"
            type="button"
            class="sz-dp-foot-btn sz-dp-foot-btn--clear"
            @click="clearDate"
          >Limpar</button>
          <span v-else />
          <button type="button" class="sz-dp-foot-btn sz-dp-foot-btn--today" @click="pickToday">Hoje</button>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<style scoped>
.sz-dp {
  display: inline-block;
  width: 100%;
  font-family: 'Inter', system-ui, sans-serif;
}
.sz-dp-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 40px;
  padding: 0 12px;
  border: 1px solid #D1D5DB;
  border-radius: 8px;
  background: #FFFFFF;
  color: #000000;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
}
.sz-dp-trigger:hover { border-color: #B7B7B7; }
.sz-dp-trigger:focus-visible { outline: 2px solid #388E3C; outline-offset: 1px; }
.sz-dp-trigger--empty .sz-dp-trigger-text { color: #A1A1A1; font-weight: 400; }
.sz-dp-trigger--disabled { background: #F5F5F5; cursor: not-allowed; opacity: 0.7; }
.sz-dp-trigger-text { flex: 1; text-align: left; }
.sz-dp-trigger-icon { color: #000000; flex-shrink: 0; margin-left: 8px; }
.dark .sz-dp-trigger-icon { color: #E5E7EB; }

/* Popover */
.sz-dp-popover {
  width: 304px;
  background: #FFFFFF;
  border: 1px solid #FFFFFF;
  border-radius: 16px;
  box-shadow: 0px 8px 32px rgba(15, 23, 42, 0.1);
  user-select: none;
}
.sz-dp-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 8px 0 16px;
  height: 56px;
  border-bottom: 1px solid #E5E7EB;
}
.sz-dp-header-selects { display: flex; align-items: center; gap: 4px; flex: 1; min-width: 0; }
.sz-dp-select-wrap { position: relative; }
.sz-dp-select {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 28px;
  padding: 0 6px;
  border: none;
  border-radius: 6px;
  background: transparent;
  font-family: inherit;
  font-size: 14px;
  font-weight: 500;
  color: #000000;
  cursor: pointer;
  transition: background 0.15s;
}
.sz-dp-select:hover { background: rgba(0,0,0,0.04); }
.sz-dp-select.open { background: rgba(0,0,0,0.06); }
.sz-dp-select svg { color: #000000; transition: transform 0.15s; }
.sz-dp-select.open svg { transform: rotate(180deg); }
.sz-dp-select-list {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  min-width: 100%;
  max-height: 220px;
  overflow-y: auto;
  padding: 4px;
  background: #FFFFFF;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.12);
  z-index: 10;
  display: flex;
  flex-direction: column;
}
.sz-dp-select-item {
  border: none;
  background: transparent;
  font-family: inherit;
  font-size: 13px;
  font-weight: 500;
  color: #000000;
  text-align: left;
  padding: 8px 10px;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.12s, color 0.12s;
  white-space: nowrap;
}
.sz-dp-select-item:hover { background: rgba(56,142,60,0.08); }
.sz-dp-select-item.active { background: #388E3C; color: #FFFFFF; }
.sz-dp-header-nav { display: flex; gap: 4px; flex-shrink: 0; }
.sz-dp-nav-btn {
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #388E3C;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;
}
.sz-dp-nav-btn:hover { background: rgba(56,142,60,0.08); }
.sz-dp-nav-btn:focus-visible { outline: 2px solid #388E3C; outline-offset: 1px; }

/* Calendar grid */
.sz-dp-calendar {
  padding: 8px 12px;
  border-bottom: 1px solid #E5E7EB;
}
.sz-dp-weekrow,
.sz-dp-week {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
}
.sz-dp-weekday {
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 400;
  color: #000000;
}
.sz-dp-cell {
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  font-family: 'Inter', system-ui, sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #000000;
  cursor: pointer;
  border-radius: 999px;
  transition: background 0.12s, color 0.12s;
  padding: 0;
}
.sz-dp-cell:hover:not(.sz-dp-cell--selected):not(.sz-dp-cell--disabled) {
  background: rgba(56,142,60,0.08);
}
.sz-dp-cell:focus-visible { outline: 2px solid #388E3C; outline-offset: -2px; }
.sz-dp-cell--out { color: #A1A1A1; }
.sz-dp-cell--today {
  outline: 1px solid #388E3C;
  outline-offset: -2px;
}
.sz-dp-cell--selected {
  background: #388E3C;
  color: #FFFFFF;
}
.sz-dp-cell--selected:hover { background: #2F7A33; }
.sz-dp-cell--disabled { color: #D1D5DB; cursor: not-allowed; }

/* Footer */
.sz-dp-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 16px;
  height: 48px;
}
.sz-dp-foot-btn {
  border: none;
  background: transparent;
  font-family: 'Inter', system-ui, sans-serif;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 8px;
  transition: background 0.15s;
}
.sz-dp-foot-btn:hover { background: rgba(0,0,0,0.04); }
.sz-dp-foot-btn--clear { color: #DC2626; }
.sz-dp-foot-btn--today { color: #000000; }

/* Dark mode */
.dark .sz-dp-trigger { background: #242528; border-color: #303138; color: #CDCDCD; }
.dark .sz-dp-trigger:hover { border-color: #3D3F43; }
.dark .sz-dp-trigger--empty .sz-dp-trigger-text { color: #92939A; }
.dark .sz-dp-trigger-icon { color: #92939A; }
.dark .sz-dp-popover { background: #17181B; border-color: #2A2C30; box-shadow: 0px 8px 32px rgba(0,0,0,0.4); }
.dark .sz-dp-header { border-bottom-color: #2A2C30; }
.dark .sz-dp-select { color: #E5E7EB; }
.dark .sz-dp-select svg { color: #E5E7EB; }
.dark .sz-dp-select:hover { background: rgba(255,255,255,0.06); }
.dark .sz-dp-select.open { background: rgba(255,255,255,0.08); }
.dark .sz-dp-select-list {
  background: #1C1E22;
  border-color: #2A2C30;
  box-shadow: 0 4px 16px rgba(0,0,0,0.5);
}
.dark .sz-dp-select-item { color: #E5E7EB; }
.dark .sz-dp-select-item:hover { background: rgba(138,206,138,0.12); }
.dark .sz-dp-nav-btn { color: #8ACE8A; }
.dark .sz-dp-nav-btn:hover { background: rgba(138,206,138,0.12); }
.dark .sz-dp-calendar { border-bottom-color: #2A2C30; }
.dark .sz-dp-weekday { color: #E5E7EB; }
.dark .sz-dp-cell { color: #E5E7EB; }
.dark .sz-dp-cell:hover:not(.sz-dp-cell--selected):not(.sz-dp-cell--disabled) {
  background: rgba(138,206,138,0.12);
}
.dark .sz-dp-cell--out { color: #6B7280; }
.dark .sz-dp-cell--disabled { color: #3A3D42; }
.dark .sz-dp-cell--selected { background: #388E3C; color: #FFFFFF; }
.dark .sz-dp-foot-btn:hover { background: rgba(255,255,255,0.06); }
.dark .sz-dp-foot-btn--today { color: #E5E7EB; }
</style>
